import { createSelector } from 'reselect'
import moment from 'moment'
import {
  zipWith,
  arrayMin,
  contains,
  indexOf,
  flatten,
  uniqueWith,
  unique,
} from 'common/utils/arrayUtils'
import { convertToCurrency } from 'common/utils/currency'
import R2RDateTime from 'common/utils/R2RDateTime/R2RDateTime'
import { convertToTransitKind } from 'common/utils/transitKinds'
import {
  TAB_ORDER_PREFERENCES,
  MODES_WITH_LEG_PAIRS,
  TRANSITKINDS_WITH_DIRECT_OFFSITE,
  CART_TYPES,
  MIN_BUFFER_MINUTES,
} from 'tickets/utils/constants'
import { getCurrencyCode, getCurrencyTypes } from 'tickets/redux/modules/user/selectors'
import {
  getSelectedOutboundModeId,
  getSelectedInboundModeId,
  getHasSelectedOutboundItinerary,
  getHasSelectedInboundItinerary,
  getSelectedOutboundFareClassNames,
  getSelectedInboundFareClassNames,
  getIsSelectingInbound,
} from 'tickets/redux/modules/selections/selectors'
import {
  getSelectedOutboundItineraryId,
  getSelectedInboundItineraryId,
  getCurrentModeId,
} from 'tickets/redux/modules/router/selectors'
import {
  getSearchIsReturn,
  getSearchIsValid,
} from 'tickets/redux/modules/searchParams/selectors'
import {
  getHasActiveModeLoadedOutbound,
  getHasActiveModeLoadedInbound,
  getHasActiveModeLoaded,
  getModeIdsToShow,
  getModesInfo,
  getIsActiveModePolling,
} from 'tickets/redux/modules/modesInfo/selectors'
import {
  getOriginShortName,
  getDestinationShortName,
} from 'tickets/redux/modules/location/selectors'
import { getAvailableModeIds, getIsError } from 'tickets/redux/modules/searchConfig/selectors'
import { createScheduledTimelineForTicketsItinerary } from 'common/toucan'

const getRawSearchResponses = state => state.searchResponses

const getSelectedModeRawSearchResponse = createSelector(
  [getRawSearchResponses, getCurrentModeId],
  (rawSearchResponses, activeModeId) => rawSearchResponses[activeModeId]
)

const getSelectedOutboundRawSearchResponse = createSelector(
  [getRawSearchResponses, getSelectedOutboundModeId],
  (rawSearchResponses, selectedOutboundModeId) => rawSearchResponses[selectedOutboundModeId]
)

const getSelectedInboundRawSearchResponse = createSelector(
  [getRawSearchResponses, getSelectedInboundModeId],
  (rawSearchResponses, selectedInboundModeId) => rawSearchResponses[selectedInboundModeId]
)

const getSearchResponses = createSelector(
  [getRawSearchResponses, getAvailableModeIds, getCurrencyCode, getCurrencyTypes],
  (rawSearchResponses, modeIds, userCurrencyCode, currencyTypes) => {
    const searchResponses = {}

    modeIds.forEach((modeId) => {
      const rawSearchResponse = rawSearchResponses[modeId]
      const response = convertResponseCurrency(rawSearchResponse, userCurrencyCode, currencyTypes)
      if (response) {
        searchResponses[modeId] = response
      }
    })
    return searchResponses
  }
)

const getSelectedModeSearchResponse = createSelector(
  [getSelectedModeRawSearchResponse, getCurrencyCode, getCurrencyTypes],
  (selectedRawResponse, currencyCode, currencyTypes) =>
    convertResponseCurrency(selectedRawResponse, currencyCode, currencyTypes)
)

const getSelectedModeRequestId = createSelector(
  [getSelectedModeRawSearchResponse], (rawResponse) => rawResponse.request.requestId
)

const getSelectedOutboundSearchResponse = createSelector(
  [getSelectedOutboundRawSearchResponse, getCurrencyCode, getCurrencyTypes],
  (selectedRawResponse, currencyCode, currencyTypes) =>
    convertResponseCurrency(selectedRawResponse, currencyCode, currencyTypes)
)

const getSelectedInboundSearchResponse = createSelector(
  [getSelectedInboundRawSearchResponse, getCurrencyCode, getCurrencyTypes],
  (selectedRawResponse, currencyCode, currencyTypes) =>
    convertResponseCurrency(selectedRawResponse, currencyCode, currencyTypes)
)

const getSelectedOutboundItineraryIndex = createSelector(
  [getSelectedOutboundSearchResponse, getSelectedOutboundItineraryId],
  (response, itineraryId) => {
    if (response == null || itineraryId == null || itineraryId == undefined) {
      return null
    }

    const idx = indexOf(response.outboundItineraries, itin => itin.itineraryId === itineraryId)

    return idx < 0 ? null : idx
  }
)

const getSelectedInboundItineraryIndex = createSelector(
  [getSelectedInboundSearchResponse, getSelectedInboundItineraryId],
  (response, itineraryId) => {
    if (response == null || itineraryId == null || itineraryId == undefined) {
      return null
    }

    const idx = indexOf(response.inboundItineraries, itin => itin.itineraryId === itineraryId)

    return idx < 0 ? null : idx
  }
)

const getSelectedOutboundItineraryRequestId = createSelector(
  [getSelectedOutboundSearchResponse, getSelectedOutboundItineraryIndex],
  (response, itineraryIndex) => {
    if (itineraryIndex === null) {
      return null
    }

    return response.outboundItineraries[itineraryIndex].requestId
  }
)

const getSelectedInboundItineraryRequestId = createSelector(
  [getSelectedInboundSearchResponse, getSelectedInboundItineraryIndex],
  (response, itineraryIndex) => {
    if (itineraryIndex === null) {
      return null
    }

    return response.inboundItineraries[itineraryIndex].requestId
  }
)

const getSelectedOutboundItineraryLegs = createSelector(
  [getSelectedOutboundSearchResponse, getSelectedOutboundItineraryIndex],
  (response, itineraryIndex) => {
    if (itineraryIndex === null) {
      return []
    }

    const itin = response.outboundItineraries[itineraryIndex]
    return itin.legs.map(legIndex => response.legs[legIndex])
  }
)

const getSelectedInboundItineraryLegs = createSelector(
  [getSelectedInboundSearchResponse, getSelectedInboundItineraryIndex],
  (response, itineraryIndex) => {
    if (itineraryIndex === null) {
      return []
    }

    const itin = response.inboundItineraries[itineraryIndex]
    return itin.legs.map(legIndex => response.legs[legIndex])
  }
)

const getSelectedOutboundItineraryProvider = createSelector(
  [getSelectedOutboundSearchResponse, getSelectedOutboundItineraryIndex],
  (response, itineraryIndex) => {
    if (itineraryIndex === null) {
      return null
    }

    const itin = response.outboundItineraries[itineraryIndex]
    return response.bookingData[itin.bookingDataIndex]
  }
)

const getSelectedInboundItineraryProvider = createSelector(
  [getSelectedInboundSearchResponse, getSelectedInboundItineraryIndex],
  (response, itineraryIndex) => {
    if (itineraryIndex === null) {
      return null
    }

    const itin = response.inboundItineraries[itineraryIndex]
    return response.bookingData[itin.bookingDataIndex]
  }
)

const getOutboundAndInboundBookableTogether = createSelector(
  [
    getSelectedOutboundItineraryProvider,
    getSelectedInboundItineraryProvider,
  ],
  (
    outboundProvider,
    inboundProvider,
  ) => {
    if (outboundProvider == null || inboundProvider == null) {
      return false
    }

    if (outboundProvider.providerName !== inboundProvider.providerName) {
      return false
    }

    return outboundProvider.itinerariesCombinableWithSelf
  }
)

const getActiveItineraries = createSelector(
  [
    getSelectedModeSearchResponse,
    getCurrentModeId,
    getIsSelectingInbound,
    getSelectedOutboundSearchResponse,
    getSelectedOutboundModeId,
    getSelectedOutboundItineraryIndex,
  ],
  (
    activeSearchResponse,
    currentModeId,
    isSelectingInbound,
    outboundResponse,
    outboundModeId,
    outboundItineraryIndex,
  ) => {
    if (!activeSearchResponse) return []

    const itineraries = isSelectingInbound
      ? activeSearchResponse.inboundItineraries
      : activeSearchResponse.outboundItineraries

    const result = []
    itineraries.forEach((itin, itinIndex) => {
      const transitKind = itin.transitKind
      let faresObj = {}
      let faress = []
      /* eslint-disable max-len */
      if (isSelectingInbound) {
        if (!isAfterBufferPeriod(outboundResponse, outboundItineraryIndex, activeSearchResponse, itinIndex)) {
          return
        }
        faresObj = getFaresForItin(outboundResponse, outboundModeId, outboundItineraryIndex, activeSearchResponse, currentModeId, itinIndex)
        faress = buildFaresForLegs(faresObj.oneWayInboundFares, faresObj.returnFares)
      } else {
        // Assume selecing outbound
        faresObj = getFaresForItin(activeSearchResponse, currentModeId, itinIndex, activeSearchResponse, currentModeId, null)
        faress = buildFaresForLegs(faresObj.oneWayOutboundFares, faresObj.returnFares)
      }
      /* eslint-enable max-len */

      if (faress.length > 0) {
        result.push(
          createItineraryForView(
            activeSearchResponse,
            currentModeId,
            itin,
            itinIndex,
            faress,
            !isSelectingInbound,
            transitKind,
          )
        )
      }
    })

    return result
  }
)

// Get all selectable fares based on selected itineraries, for each leg in the itinerary
const getFaresForSelectedItineraries = createSelector(
  [
    getSearchIsReturn,
    getSelectedOutboundSearchResponse,
    getSelectedOutboundModeId,
    getSelectedOutboundItineraryIndex,
    getHasSelectedOutboundItinerary,
    getSelectedInboundSearchResponse,
    getSelectedInboundModeId,
    getSelectedInboundItineraryIndex,
    getHasSelectedInboundItinerary,
  ],
  (
    isReturnSearch,
    outboundResponse,
    outboundModeId,
    outItinIdx,
    hasOutbound,
    inboundResponse,
    inboundModeId,
    inItinIdx,
    hasInbound,
  ) => {
    const noFaresObj = {
      oneWayOutboundFares: [],
      oneWayInboundFares: [],
      returnFares: [],
    }
    if (!isReturnSearch && !hasOutbound) {
      return noFaresObj
    } else if (isReturnSearch && !hasOutbound && !hasInbound) {
      return noFaresObj
    }

    return getFaresForItin(outboundResponse, outboundModeId, outItinIdx,
      inboundResponse, inboundModeId, inItinIdx)
  }
)

const getOutboundTicket = createSelector(
  [
    getSelectedOutboundSearchResponse,
    getSelectedOutboundModeId,
    getSelectedOutboundItineraryIndex,
    getSelectedInboundSearchResponse,
    getSelectedInboundModeId,
    getSelectedInboundItineraryIndex,
  ],
  (
    outboundResponse,
    outboundModeId,
    outboundItineraryIndex,
    inboundResponse,
    inboundModeId,
    inboundItineraryIndex,
  ) => {
    if (outboundItineraryIndex === null) {
      return null
    }

    const faresObj = getFaresForItin(outboundResponse, outboundModeId, outboundItineraryIndex,
      inboundResponse, inboundModeId, inboundItineraryIndex)
    const faress = buildFaresForLegs(faresObj.oneWayOutboundFares, faresObj.returnFares)
    const transitKind = outboundResponse.outboundItineraries[outboundItineraryIndex].transitKind

    return createItineraryForView(
      outboundResponse,
      outboundModeId,
      outboundResponse.outboundItineraries[outboundItineraryIndex],
      outboundItineraryIndex,
      faress,
      true,
      transitKind,
    )
  }
)

const getInboundTicket = createSelector(
  [
    getSelectedInboundSearchResponse,
    getSelectedInboundModeId,
    getSelectedInboundItineraryIndex,
    getSelectedOutboundSearchResponse,
    getSelectedOutboundModeId,
    getSelectedOutboundItineraryIndex,
  ],
  (
    inboundResponse,
    inboundModeId,
    inboundItineraryIndex,
    outboundResponse,
    outboundModeId,
    outboundItineraryIndex,
  ) => {
    if (inboundItineraryIndex === null) {
      return null
    }

    const faresObj = getFaresForItin(outboundResponse, outboundModeId, outboundItineraryIndex,
      inboundResponse, inboundModeId, inboundItineraryIndex)
    const faress = buildFaresForLegs(faresObj.oneWayInboundFares, faresObj.returnFares)
    const transitKind = inboundResponse.inboundItineraries[inboundItineraryIndex].transitKind

    return createItineraryForView(
      inboundResponse,
      inboundModeId,
      inboundResponse.inboundItineraries[inboundItineraryIndex],
      inboundItineraryIndex,
      faress,
      false,
      transitKind,
    )
  }
)

const getActiveInboundFareClassNames = createSelector(
  [getSelectedInboundFareClassNames, getInboundTicket],
  (selectedInboundFareClassNames, inboundTicket) => (
    (inboundTicket && selectedInboundFareClassNames.length == 0)
      ? inboundTicket.minPriceFareClassNames
      : selectedInboundFareClassNames
  )
)

const getActiveOutboundFareClassNames = createSelector(
  [getSelectedOutboundFareClassNames, getOutboundTicket],
  (selectedOutboundFareClassNames, outboundTicket) => (
    (outboundTicket && selectedOutboundFareClassNames.length == 0)
      ? outboundTicket.minPriceFareClassNames
      : selectedOutboundFareClassNames
  )
)

// Get all selectable fares based on the users current selections, for each leg in the itinerary
const getFaresForSelections = createSelector(
  [
    getSearchIsReturn,
    getSelectedOutboundSearchResponse,
    getSelectedOutboundModeId,
    getSelectedOutboundItineraryIndex,
    getActiveOutboundFareClassNames,
    getHasSelectedOutboundItinerary,
    getSelectedInboundSearchResponse,
    getSelectedInboundModeId,
    getSelectedInboundItineraryIndex,
    getActiveInboundFareClassNames,
    getHasSelectedInboundItinerary,
  ],
  (
    isReturnSearch,
    outboundResponse,
    outboundModeId,
    outItinIdx,
    outboundFareClasses,
    hasOutbound,
    inboundResponse,
    inboundModeId,
    inItinIdx,
    inboundFareClasses,
    hasInbound,
  ) => {
    const noFaresObj = {
      oneWayOutboundFares: [],
      oneWayInboundFares: [],
      returnFares: [],
    }
    if (!isReturnSearch && !hasOutbound) {
      return noFaresObj
    } else if (isReturnSearch && !hasOutbound && !hasInbound) {
      return noFaresObj
    }

    const faresObj = getFaresForItin(outboundResponse, outboundModeId, outItinIdx,
      inboundResponse, inboundModeId, inItinIdx)
    const oneWayOutboundFares =
      filterFaresByClass(outboundFareClasses, faresObj.oneWayOutboundFares)
    const oneWayInboundFares =
      filterFaresByClass(inboundFareClasses, faresObj.oneWayInboundFares)
    const returnFares =
      filterFaresByClass(outboundFareClasses, faresObj.returnFares)

    return {
      oneWayOutboundFares,
      oneWayInboundFares,
      returnFares,
    }
  }
)

const getSelectedOutboundFares = createSelector(
  [getFaresForSelections],
  (allFares) => {
    // Note: There might be multiple fares listed for a leg if multiple agents
    // can sell the same fare, this is why we just take the first (cheapest) one
    const faresForLegs = allFares.oneWayOutboundFares.map(fares => fares[0])
    return flatten(faresForLegs)
  }
)

// includes return
const getSelectedInboundFares = createSelector(
  [getFaresForSelections],
  (allFares) => {
    // Note: There might be multiple fares listed for a leg if multiple agents
    // can sell the same fare, this is why we just take the first (cheapest) one
    const inboundFaresForLegs = allFares.oneWayInboundFares.map(fares => fares[0])
    const returnFaresForLegs = allFares.returnFares.map(fares => fares[0])
    return flatten(inboundFaresForLegs.concat(returnFaresForLegs))
  }
)

const getSelectedOutboundFareNames = createSelector(
  [getSelectedOutboundFares],
  (outboundFares) => outboundFares.map(fare => fare.name)
)

const getSelectedInboundFareNames = createSelector(
  [getSelectedInboundFares],
  (inboundFares) => inboundFares.map(fare => fare.name)
)

const getSelectedOutboundFareRules = createSelector(
  [getSelectedOutboundFares],
  (outboundFares) => {
    const faresWithRules = outboundFares.filter(fare => fare.fareRules && fare.fareRules.length)
    return flatten(faresWithRules.map(fare => fare.fareRules))
  }
)

const getSelectedOutboundFareRulesSummary = createSelector(
  [getSelectedOutboundFares],
  (outboundFares) => {
    const faresWithRulesSummary = outboundFares.filter(
      fare => fare.rulesSummary
    )
    return flatten(faresWithRulesSummary.map(fare => fare.rulesSummary))
  }
)

const getSelectedInboundFareRules = createSelector(
  [getSelectedInboundFares],
  (inboundFares) => {
    const faresWithRules = inboundFares.filter(fare => fare.fareRules && fare.fareRules.length)
    return flatten(faresWithRules.map(fare => fare.fareRules))
  }
)

const getSelectedInboundFareRulesSummary = createSelector(
  [getSelectedInboundFares],
  (inboundFares) => {
    const faresWithRulesSummary = inboundFares.filter(
      fare => fare.rulesSummary
    )
    return flatten(faresWithRulesSummary.map(fare => fare.rulesSummary))
  }
)

const getSelectedOutboundFareIndices = createSelector(
  [getFaresForSelectedItineraries, getActiveOutboundFareClassNames],
  (allFares, selectedFareNamesForLegs) => {
    const faresForLegs = allFares.oneWayOutboundFares
    return zipWith(
      [faresForLegs, selectedFareNamesForLegs],
      (fares, fareName) => {
        const fareNamesForLeg = fares.map((fare) => fare.name)
        return fareNamesForLeg.indexOf(fareName)
      }
    )
  }
)

const getSelectedInboundFareIndices = createSelector(
  [getFaresForSelectedItineraries, getActiveInboundFareClassNames],
  (allFares, selectedFareNamesForLegs) => {
    const faresForLegs = allFares.oneWayInboundFares
    return zipWith(
      [faresForLegs, selectedFareNamesForLegs],
      (fares, fareName) => {
        const fareNamesForLeg = fares.map((fare) => fare.name)
        return fareNamesForLeg.indexOf(fareName)
      }
    )
  }
)

const getSelectedOutboundItineraryLegTitles = createSelector(
  [getSelectedOutboundSearchResponse, getSelectedOutboundItineraryIndex],
  (response, itinIndex) => {
    const itinerary = response.outboundItineraries[itinIndex]

    const names = []
    itinerary.legs.forEach(legIndex => {
      const fromName = getDepartingLocationFromLeg(legIndex, response)
      const toName = getArrivalLocationFromLeg(legIndex, response)
      return names.push(`${fromName} to ${toName}`)
    })

    return names
  }
)

const getSelectedInboundItineraryLegTitles = createSelector(
  [getSelectedInboundSearchResponse, getSelectedInboundItineraryIndex],
  (response, itinIndex) => {
    const itinerary = response.inboundItineraries[itinIndex]

    const names = []
    itinerary.legs.forEach(legIndex => {
      const fromName = getDepartingLocationFromLeg(legIndex, response)
      const toName = getArrivalLocationFromLeg(legIndex, response)
      return names.push(`${fromName} to ${toName}`)
    })

    return names
  }
)

// Does the active mode have any results?
const getActiveModeHasResults = createSelector(
  [getSelectedModeSearchResponse, getSearchIsReturn],
  (searchResponse, isReturnSearch) => {
    if (!searchResponse) {
      return false
    }

    const outboundLength = searchResponse.outboundItineraries.length
    const inboundLength = searchResponse.inboundItineraries.length

    if (isReturnSearch) {
      return !!(outboundLength && inboundLength)
    }

    return !!outboundLength
  }
)

// Does the active mode have no results even after trying to load?
const getActiveModeHasNoResults = createSelector(
  [
    getHasActiveModeLoaded,
    getSelectedModeSearchResponse,
    getSearchIsReturn,
    getSearchIsValid,
  ],
  (modeHasLoaded, searchResponse, isReturnSearch, isSearchValid) => {
    if (!isSearchValid) {
      return true
    }

    if (!modeHasLoaded) {
      // if it's loading, then it doesn't have "no results"
      return false
    }
    if (modeHasLoaded && !searchResponse) {
      return true
    }
    if (isReturnSearch) {
      return !searchResponse.outboundItineraries.length || !searchResponse.inboundItineraries.length
    }

    return !searchResponse.outboundItineraries.length
  }
)

// Used for logging
const getSelectedOutboundOriginDestination = createSelector(
  [getSelectedOutboundSearchResponse, getSelectedOutboundItineraryIndex],
  (response, outboundItineraryIndex) => {
    if (outboundItineraryIndex === null) {
      return null
    }

    const itinerary = response.outboundItineraries[outboundItineraryIndex]

    return getItineraryOriginDestination(response, itinerary)
  }
)

// Used for logging
const getSelectedInboundOriginDestination = createSelector(
  [getSelectedInboundSearchResponse, getSelectedInboundItineraryIndex],
  (response, inboundItineraryIndex) => {
    if (inboundItineraryIndex === null) {
      return null
    }

    const itinerary = response.inboundItineraries[inboundItineraryIndex]

    return getItineraryOriginDestination(response, itinerary)
  }
)

// Are there return fares for the selected itineraries
const getSelectedFaresAreReturn = createSelector(
  [getFaresForSelections],
  (faresForSelections) => {
    // We should only have one-way fares or return fares, never both.
    return !!faresForSelections.returnFares.length
  }
)

const getTabs = createSelector(
  [
    getSearchIsReturn,
    getModeIdsToShow,
    getModesInfo,
    getCurrentModeId,
    getSearchResponses,
    getIsError,
    getCurrencyCode,
    getCurrencyTypes,
    getSearchIsValid,
  ],
  (
    isReturn,
    modeIds,
    modesInfo,
    currentModeId,
    searchResponses,
    isError,
    currencyCode,
    currencyTypes,
    isSearchValid,
  ) => {
    const tabs = modeIds.map(modeName => {
      const modeInfo = modesInfo[modeName]

      const modeSearchResponse = searchResponses[modeName]
      const hasResponse = modeSearchResponse && Object.keys(modeSearchResponse).length > 0
      const outboundItineraries = hasResponse ? modeSearchResponse.outboundItineraries : []
      const inboundItineraries = hasResponse ? modeSearchResponse.inboundItineraries : []
      const fares = hasResponse ? modeSearchResponse.fares : []

      const minPrice = getTabMinPrice(outboundItineraries, inboundItineraries,
        fares, isReturn, currencyCode, currencyTypes)

      const hasLoaded = modeInfo.hasLoadedOutbound && modeInfo.hasLoadedInbound

      return {
        name: modeInfo.name,
        isLoading: isSearchValid && !isError && !hasLoaded,
        messages: modeInfo.messages,
        isDisabled: !getTabHasResults(isReturn, outboundItineraries, inboundItineraries),
        minPrice,
      }
    })

    // This is to make sure that the mode specified in initial URl
    // initially shows up as a tab that's loading
    if (currentModeId && currentModeId != 'noResults' && !modeIds.includes(currentModeId)) {
      tabs.push({
        name: currentModeId,
        isLoading: isSearchValid && !isError,
        messages: [],
        isDisabled: true,
        minPrice: null,
      })
    }

    return tabs.sort(compareTabs)
  }
)

const getActiveModeIsLoadingOutbound = createSelector(
  [
    getHasActiveModeLoadedOutbound,
    getIsActiveModePolling,
    getSearchIsValid,
    getActiveModeHasResults,
  ],
  (hasLoadedOutbound, isPolling, isSearchValid, hasResults) => {
    if (isPolling) {
      return !hasResults
    }
    return !!(isSearchValid && !hasLoadedOutbound)
  }
)

const getActiveModeIsLoadingInbound = createSelector(
  [
    getHasActiveModeLoadedInbound,
    getIsActiveModePolling,
    getSearchIsValid,
    getActiveModeHasResults,
  ],
  (hasLoadedInbound, isPolling, isSearchValid, hasResults) => {
    if (isPolling) {
      return !hasResults
    }
    return !!(isSearchValid && !hasLoadedInbound)
  }
)

const getActiveModeIsLoading = createSelector(
  [getHasActiveModeLoaded, getIsActiveModePolling, getSearchIsValid, getActiveModeHasResults],
  (hasLoaded, isPolling, isSearchValid, hasResults) => {
    if (isPolling) {
      return !hasResults
    }
    return !!(isSearchValid && !hasLoaded)
  }
)

const compareTabs = (a, b) => {
  const aOrderScore = TAB_ORDER_PREFERENCES[a.name] || 0
  const bOrderScore = TAB_ORDER_PREFERENCES[b.name] || 0
  return aOrderScore < bOrderScore ? 1 : -1
}

const showOutboundDirection = createSelector(
  [getSearchIsValid, getIsError, getActiveModeHasNoResults],
  (isSearchValid, isError, activeModeHasNoResults) =>
    (isSearchValid && !isError && !activeModeHasNoResults)
)

const showInboundDirection = createSelector(
  [getSearchIsReturn, getHasSelectedOutboundItinerary, showOutboundDirection],
  (searchIsReturn, hasSelectedOutbound, showOutbound) =>
    (searchIsReturn && hasSelectedOutbound && showOutbound)
)

const getLastOutboundTicketTime = createSelector(
  [getSelectedModeSearchResponse],
  (response) =>
    getLastItineraryDepartureTime(response.outboundItineraries, response.legs, response.hops)
)

const getLastInboundTicketTime = createSelector(
  [getSelectedModeSearchResponse],
  (response) =>
    getLastItineraryDepartureTime(response.inboundItineraries, response.legs, response.hops)
)

const getDetailsLinkForSelections = createSelector(
  [
    getSelectedModeSearchResponse,
    getSelectedOutboundItineraryIndex,
    getSelectedInboundItineraryIndex,
  ],
  (searchResponse, outboundItineraryId, inboundItineraryId) => {
    // Assumption: If have to fetch final prices and user has done return search,
    // the ticket is a return ticket. This is because the SkyScanner provider is the only
    // one that needs to fetch final prices, and they only have return tickets on return searches.
    const compatibleDetailsLinks = searchResponse.detailsLinks.filter(
      (detailsLink) => {
        const isOutboundMatch = detailsLink.outboundItinerary == outboundItineraryId
        const isInboundMatch = detailsLink.inboundItinerary == inboundItineraryId
        return isOutboundMatch && isInboundMatch
      }
    )

    // Assumption: there will only be one compatible link for each ticket (or ticket pair)
    return compatibleDetailsLinks[0]
  }
)

const getNeedsDetailsLookup = createSelector(
  [getSearchIsReturn, getOutboundTicket, getInboundTicket],
  (isReturnSearch, outboundTicket, inboundTicket) => {
    const outboundNeedsDetailsUpdate = !!(outboundTicket && outboundTicket.detailsLink)
    const inboundNeedsDetailsUpdate = !!(inboundTicket && inboundTicket.detailsLink)
    if (isReturnSearch) {
      return outboundNeedsDetailsUpdate && inboundNeedsDetailsUpdate
    }
    return outboundNeedsDetailsUpdate
  }
)

const getSelectedOutboundMinPrice = createSelector(
  [getFaresForSelectedItineraries],
  (fares) => {
    const outboundFaress = fares.oneWayOutboundFares
    const minOneWayFare = outboundFaress.map(
      outboundFares => arrayMin(outboundFares, f => f.price)
    ).reduce((acc, f) => acc + f, 0)

    const returnFaress = fares.returnFares
    const minReturnFare = returnFaress.map(
      outboundFares => arrayMin(outboundFares, f => f.price)
    ).reduce((acc, f) => acc + f, 0)

    if (minOneWayFare && minReturnFare) {
      return Math.min(minOneWayFare, minReturnFare)
    }

    // One of these is guaranteed to be non zero
    return minOneWayFare ? minOneWayFare : minReturnFare
  }
)

const getSelectedOutboundOriginalMinPrice = createSelector(
  [getFaresForSelectedItineraries],
  (fares) => {
    const outboundFaress = fares.oneWayOutboundFares
    const minOneWayFare = outboundFaress.map(
      outboundFares => arrayMin(outboundFares, f => f.originalPrice)
    ).reduce((acc, f) => acc + f, 0)

    const returnFaress = fares.returnFares
    const minReturnFare = returnFaress.map(
      outboundFares => arrayMin(outboundFares, f => f.originalPrice)
    ).reduce((acc, f) => acc + f, 0)

    if (minOneWayFare && minReturnFare) {
      return Math.min(minOneWayFare, minReturnFare)
    }

    // One of these is guaranteed to be non zero
    return minOneWayFare ? minOneWayFare : minReturnFare
  }
)

const getSelectedInboundMinPrice = createSelector(
  [getFaresForSelectedItineraries],
  (fares) => {
    const inboundFaress = fares.oneWayInboundFares
    const minOneWayFare = inboundFaress.map(
      outboundFares => arrayMin(outboundFares, f => f.price)
    ).reduce((acc, f) => acc + f, 0)

    const returnFaress = fares.returnFares
    const minReturnFare = returnFaress.map(
      outboundFares => arrayMin(outboundFares, f => f.price)
    ).reduce((acc, f) => acc + f, 0)

    if (minOneWayFare && minReturnFare) {
      return Math.min(minOneWayFare, minReturnFare)
    }

    // One of these is guaranteed to be non zero
    return minOneWayFare ? minOneWayFare : minReturnFare
  }
)

const getSelectedOutboundFareExtraCost = createSelector(
  [getFaresForSelections, getSelectedOutboundMinPrice, getSelectedFaresAreReturn],
  (selectedFares, minPrice) => {
    // Note: return fares are shown on inbounds
    const selectedOutboundFares = selectedFares.oneWayOutboundFares
    const selectedPrice = selectedOutboundFares.map(
      fares => arrayMin(fares, f => f.price)
    ).reduce((acc, f) => acc + f, 0)

    return selectedPrice - minPrice
  }
)

const getSelectedInboundFareExtraCost = createSelector(
  [getFaresForSelections, getSelectedInboundMinPrice, getSelectedFaresAreReturn],
  (selectedFares, minPrice, selectedFaresAreReturn) => {
    if (selectedFaresAreReturn) {
      const selectedReturnFares = selectedFares.returnFares
      const selectedPrice = selectedReturnFares.map(
        fares => arrayMin(fares, f => f.originalPrice)
      ).reduce((acc, f) => acc + f, 0)
      return selectedPrice - minPrice
    }

    const selectedInboundFares = selectedFares.oneWayInboundFares
    const selectedPrice = selectedInboundFares.map(
      fares => arrayMin(fares, f => f.originalPrice)
    ).reduce((acc, f) => acc + f, 0)
    return selectedPrice - minPrice
  }
)

// The aim of this selector is to get the price to compare inbound return fares against
const getSelectedInboundBasePrice = createSelector(
  [
    getSelectedOutboundSearchResponse,
    getSelectedOutboundModeId,
    getSelectedOutboundItineraryIndex,
    getSelectedOutboundOriginalMinPrice,
  ],
  (
    response,
    modeId,
    itineraryIndex,
    outboundMinPrice,
  ) => {
    if (!response || itineraryIndex == null) {
      return null
    }

    const faresObj = getFaresForItin(response, modeId, itineraryIndex, response, modeId, null)
    if (faresObj.returnFares.length == 0) {
      return null
    }

    return outboundMinPrice
  }
)

const getSelectedOutboundIsDirectBooking = createSelector(
  [
    getSelectedOutboundItineraryIndex,
    getSelectedOutboundSearchResponse,
  ],
  (itinIndex, response) => {
    const itinerary = response.outboundItineraries[itinIndex]
    const bookingDataIndex = itinerary.bookingDataIndex
    return response.bookingData[bookingDataIndex].isDirectBooking
  }
)

const getSelectedInboundIsDirectBooking = createSelector(
  [
    getSelectedInboundItineraryIndex,
    getSelectedInboundSearchResponse,
  ],
  (itinIndex, response) => {
    const itinerary = response.inboundItineraries[itinIndex]
    const bookingDataIndex = itinerary.bookingDataIndex
    return response.bookingData[bookingDataIndex].isDirectBooking
  }
)

const getSelectedOutboundDepartureDateTime = createSelector(
  [
    getSelectedOutboundItineraryIndex,
    getSelectedOutboundSearchResponse,
  ],
  (itinIndex, response) => getDepartureDateTimeFromItinerary(itinIndex, true, response)
)

const getSelectedInboundDepartureDateTime = createSelector(
  [
    getSelectedInboundItineraryIndex,
    getSelectedInboundSearchResponse,
  ],
  (itinIndex, response) => getDepartureDateTimeFromItinerary(itinIndex, false, response)
)

const getSelectedOutboundArrivalDateTime = createSelector(
  [
    getSelectedOutboundItineraryIndex,
    getSelectedOutboundSearchResponse,
  ],
  (itinIndex, response) => getArrivalDateTimeFromItinerary(itinIndex, true, response)
)

const getSelectedInboundArrivalDateTime = createSelector(
  [
    getSelectedInboundItineraryIndex,
    getSelectedInboundSearchResponse,
  ],
  (itinIndex, response) => getArrivalDateTimeFromItinerary(itinIndex, false, response)
)

const getSelectedOutboundDuration = createSelector(
  [
    getSelectedOutboundItineraryIndex,
    getSelectedOutboundSearchResponse,
  ],
  (itinIndex, response) => getDurationFromItinerary(itinIndex, true, response)
)

const getSelectedInboundDuration = createSelector(
  [
    getSelectedInboundItineraryIndex,
    getSelectedInboundSearchResponse,
  ],
  (itinIndex, response) => getDurationFromItinerary(itinIndex, false, response)
)

const getSelectedOutboundStopCount = createSelector(
  [
    getSelectedOutboundItineraryIndex,
    getSelectedOutboundSearchResponse,
  ],
  (itinIndex, response) => getStopCountFromItinerary(itinIndex, true, response)
)

const getSelectedInboundStopCount = createSelector(
  [
    getSelectedInboundItineraryIndex,
    getSelectedInboundSearchResponse,
  ],
  (itinIndex, response) => getStopCountFromItinerary(itinIndex, false, response)
)

const getSelectedOutboundLogo = createSelector(
  [getSelectedOutboundItineraryIndex, getSelectedOutboundSearchResponse],
  (itinIndex, response) => getIconFromItinerary(itinIndex, true, response)
)

const getSelectedInboundLogo = createSelector(
  [getSelectedInboundItineraryIndex, getSelectedInboundSearchResponse],
  (itinIndex, response) => getIconFromItinerary(itinIndex, false, response)
)

const getSelectedOutboundHasMultipleCarriers = createSelector(
  [getSelectedOutboundItineraryIndex, getSelectedOutboundSearchResponse],
  (itinIndex, response) => getCarrierCountFromItinerary(itinIndex, true, response) > 1
)

const getSelectedInboundHasMultipleCarriers = createSelector(
  [getSelectedInboundItineraryIndex, getSelectedInboundSearchResponse],
  (itinIndex, response) => getCarrierCountFromItinerary(itinIndex, false, response) > 1
)

const getSelectedOutboundOperatorName = createSelector(
  [getSelectedOutboundItineraryIndex, getSelectedOutboundSearchResponse],
  (itinIndex, response) => getOperatorNameFromItinerary(itinIndex, true, response)
)

const getSelectedInboundOperatorName = createSelector(
  [getSelectedInboundItineraryIndex, getSelectedInboundSearchResponse],
  (itinIndex, response) => getOperatorNameFromItinerary(itinIndex, false, response)
)

const getSelectedOutboundTimeline = createSelector(
  [getSelectedOutboundItineraryIndex, getSelectedOutboundSearchResponse],
  (itinIndex, response) => getTimelineFromItinerary(itinIndex, true, response)
)

const getSelectedInboundTimeline = createSelector(
  [getSelectedInboundItineraryIndex, getSelectedInboundSearchResponse],
  (itinIndex, response) => getTimelineFromItinerary(itinIndex, false, response)
)

const getSelectedOutboundDepartingLocation = createSelector(
  [getSelectedOutboundItineraryIndex, getSelectedOutboundSearchResponse],
  (itinIndex, response) => getDepartingLocationFromItinerary(itinIndex, true, response)
)

const getSelectedInboundDepartingLocation = createSelector(
  [getSelectedInboundItineraryIndex, getSelectedInboundSearchResponse],
  (itinIndex, response) => getDepartingLocationFromItinerary(itinIndex, false, response)
)

const getSelectedOutboundArrivalLocation = createSelector(
  [getSelectedOutboundItineraryIndex, getSelectedOutboundSearchResponse],
  (itinIndex, response) => getArrivalLocationFromItinerary(itinIndex, true, response)
)

const getSelectedInboundArrivalLocation = createSelector(
  [getSelectedInboundItineraryIndex, getSelectedInboundSearchResponse],
  (itinIndex, response) => getArrivalLocationFromItinerary(itinIndex, false, response)
)

const getSelectedOutboundHasFareOptions = createSelector(
  [getFaresForSelectedItineraries],
  (fares) => {
    let outboundFaresPerLeg
    if (fares.oneWayOutboundFares.length && fares.returnFares.length) {
      outboundFaresPerLeg = zipWith(
        [fares.oneWayOutboundFares, fares.returnFares],
        (oneWayFaresForLeg, returnFaresForLeg) => oneWayFaresForLeg.concat(returnFaresForLeg)
      )
    } else if (fares.oneWayOutboundFares.length) {
      outboundFaresPerLeg = fares.oneWayOutboundFares
    } else {
      outboundFaresPerLeg = fares.returnFares
    }

    const uniqueFaresPerLeg = outboundFaresPerLeg.map(
      faresForLeg => uniqueWith(faresForLeg, (f1, f2) => f1.name === f2.name)
    )
    return uniqueFaresPerLeg.filter((faresForLeg) => faresForLeg.length > 1).length > 0
  }
)

const getSelectedInboundHasFareOptions = createSelector(
  [getFaresForSelectedItineraries],
  (fares) => {
    let inboundFaresPerLeg
    if (fares.oneWayInboundFares.length && fares.returnFares.length) {
      inboundFaresPerLeg = zipWith(
        [fares.oneWayInboundFares, fares.returnFares],
        (oneWayFaresForLeg, returnFaresForLeg) => oneWayFaresForLeg.concat(returnFaresForLeg)
      )
    } else if (fares.oneWayOutboundFares.length) {
      inboundFaresPerLeg = fares.oneWayInboundFares
    } else {
      inboundFaresPerLeg = fares.returnFares
    }

    const uniqueFaresPerLeg = inboundFaresPerLeg.map(
      faresForLeg => uniqueWith(faresForLeg, (f1, f2) => f1.name === f2.name)
    )
    return uniqueFaresPerLeg.filter((faresForLeg) => faresForLeg.length > 1).length > 0
  }
)

const getCartType = createSelector(
  [getSearchIsReturn, getOutboundAndInboundBookableTogether, getSelectedFaresAreReturn],
  (isReturnSearch, areBookableTogether, isFareReturn) => {
    let type
    if (!isReturnSearch) {
      type = CART_TYPES.oneway
    } else if (isFareReturn) {
      type = CART_TYPES.return
    } else if (areBookableTogether) {
      type = CART_TYPES.combined
    } else {
      type = CART_TYPES.separate
    }
    return type
  }
)

const getOutboundCartShouldShowBookDeal = createSelector(
  [getCartType],
  (cartType) => !!(cartType === CART_TYPES.oneway || cartType === CART_TYPES.separate)
)

const getOutboundCartShouldShowFareInfo = createSelector(
  [getCartType],
  (cartType) => !(cartType == CART_TYPES.return)
)

const getIsInvalidHistoryState = createSelector(
  [
    getHasSelectedOutboundItinerary,
    getSelectedOutboundItineraryIndex,
    getHasSelectedInboundItinerary,
    getSelectedInboundItineraryIndex,
  ],
  (
    hasSelectedOutboundItinerary,
    selectedOutboundItineraryIndex,
    hasSelectedInboundItinerary,
    selectedInboundItineraryIndex,
  ) => {
    const invalidOutbound = (hasSelectedOutboundItinerary && selectedOutboundItineraryIndex == null)
    const invalidInbound =  (hasSelectedInboundItinerary && selectedInboundItineraryIndex == null)
    return invalidOutbound || invalidInbound
  }

)

const getShouldShowCart = createSelector(
  [
    getIsInvalidHistoryState,
    getSearchIsReturn,
    getHasSelectedOutboundItinerary,
    getHasSelectedInboundItinerary,
  ],
  (isInvalidHistoryState, isSearchReturn, hasSelectedOutbound, hasSelectedInbound) => {
    const shouldShowCart = (
      (isSearchReturn ? hasSelectedOutbound && hasSelectedInbound : hasSelectedOutbound)
      && !isInvalidHistoryState
    )
    return shouldShowCart
  }
)

const getTotalCost = createSelector(
  [getFaresForSelections, getShouldShowCart],
  (faresObj, shouldShowCart) => {
    let oneWayCost
    if (faresObj.oneWayOutboundFares && !containsEmptyArray(faresObj.oneWayOutboundFares)) {
      const outboundFares = faresObj.oneWayOutboundFares
      oneWayCost = outboundFares
        .map(fares => arrayMin(fares, f => (shouldShowCart ? f.price : f.originalPrice)))
        .reduce((acc, p) => acc + p, 0)
    }
    if (faresObj.oneWayInboundFares && !containsEmptyArray(faresObj.oneWayInboundFares)) {
      const inboundFares = faresObj.oneWayInboundFares
      oneWayCost += inboundFares
        .map(fares => arrayMin(fares, f => (shouldShowCart ? f.price : f.originalPrice)))
        .reduce((acc, p) => acc + p, 0)
    }

    let returnCost
    if (faresObj.returnFares && !containsEmptyArray(faresObj.returnFares)) {
      const returnFares = faresObj.returnFares
      returnCost = returnFares
        .map(fares => arrayMin(fares, f => (shouldShowCart ? f.price : f.originalPrice)))
        .reduce((acc, p) => acc + p, 0)
    }

    if (oneWayCost && returnCost) {
      return Math.min(oneWayCost, returnCost)
    } else if (oneWayCost) {
      return oneWayCost
    } else if (returnCost) {
      return returnCost
    }

    return 0
  }
)

/* ***************************************** */
/* **************** HELPERS **************** */

// Convert the fares in a response to the given currency code
const convertResponseCurrency = (rawResponse, currencyCode, currencyTypes) => {
  if (!rawResponse) {
    return null
  }

  return {
    ...rawResponse,
    fares: convertFaresPrices(rawResponse.fares, currencyCode, currencyTypes),
  }
}

const getLastItineraryDepartureTime = (itineraries, legs, hops) => {
  const itin = itineraries[itineraries.length - 1]
  const leg = legs[itin.legs[0]]
  const hop = hops[leg.hops[0]]

  const date = hop.departureDate
  const time = hop.departureTime

  return moment(`${date}T${time}`)
}

// Prunes a list of list of fares per leg by class
const filterFaresByClass = (classes, faress) => {
  if (classes.length !== faress.length) {
    return []
  }

  return zipWith([classes, faress], (fareClass, fares) =>
    fares.filter(f => f.name === fareClass))
}

const containsEmptyArray = (array) => contains(array, x => x.length === 0)

// Filters fares based on itinerary indices
const getFaresForItin = (
  outboundResponse,
  outboundModeId,
  outItinIdx,
  inboundResponse,
  inboundModeId,
  inItinIdx,
) => {
  const hasOutbound = outItinIdx != null
  const hasInbound = inItinIdx != null

  let oneWayOutboundFares = []
  let oneWayInboundFares = []
  let outboundItinerary
  let inboundItinerary
  if (hasOutbound) {
    outboundItinerary = outboundResponse.outboundItineraries[outItinIdx]
    const legFares = outboundItinerary.legs.map(leg => filterFares(outboundResponse.fares, leg))
    // If any of the legs have no fares, the itinerary doesn't have valid fares
    if (!containsEmptyArray(legFares)) {
      oneWayOutboundFares = legFares
    }
  }
  if (hasInbound) {
    inboundItinerary = inboundResponse.inboundItineraries[inItinIdx]
    const legFares = inboundItinerary.legs.map(leg => filterFares(inboundResponse.fares, leg))
    // If any of the legs have no fares, the itinerary doesn't have valid fares
    if (!containsEmptyArray(legFares)) {
      oneWayInboundFares = legFares
    }
  }

  // NOTE: We are only dealing with leg pairs for planes right now
  // TODO: update this once we're dealing with other modes' leg pairs
  // Based on the itineraries we have, work out how we're going to match leg pair fares
  let legPairMatch
  let returnFares = []

  if (validLegpair(outboundModeId, inboundModeId)) {
    if (hasOutbound && hasInbound) {
      legPairMatch = (legPair, oLeg, iLeg) =>
        legPair.outboundLeg === oLeg && legPair.inboundLeg === iLeg
    } else if (hasOutbound && !hasInbound) {
      /* eslint-disable no-unused-vars */
      legPairMatch = (legPair, oLeg, _) => legPair.outboundLeg === oLeg
      /* eslint-enable no-unused-vars */
    } else if (!hasOutbound && hasInbound) {
      legPairMatch = (legPair, _, iLeg) => legPair.inboundLeg === iLeg
    } else {
      // Haven't selected anything
    }

    // Find compatible leg pair fares
    if (inboundModeId == null || outboundModeId == inboundModeId) {
      returnFares = outboundResponse.fares.filter(f => {
        if (!f.legPairs) {
          return false
        }

        for (let i = 0; i < f.legPairs.length; i++) {
          const legPair = f.legPairs[i]
          const oLeg = outboundItinerary && outboundItinerary.legs[0]
          const iLeg = inboundItinerary && inboundItinerary.legs[0]
          if (legPairMatch(legPair, oLeg, iLeg)) {
            return true
          }
        }

        return false
      })
    }

    // Note: We assume that itineraries with legpairs will only involve one leg, so returnFares is
    // a list of fares for the first leg, but one way fares are a list of list of fares, so we want
    // to turn returnFares also into a list of list of fares
    if (returnFares.length > 0) {
      returnFares = [returnFares]
    }
  }

  return {
    oneWayOutboundFares,
    oneWayInboundFares,
    returnFares,
  }
}

const validLegpair = (outboundModeId, inboundModeId) => {
  const outboundModeValidMode = MODES_WITH_LEG_PAIRS.includes(outboundModeId)
  const inboundModeValidMode = MODES_WITH_LEG_PAIRS.includes(inboundModeId)
  return outboundModeValidMode && (inboundModeId == null ||
    (outboundModeValidMode == inboundModeValidMode))
}

const buildFaresForLegs = (oneWayFares, returnFares) => {
  if (!oneWayFares || oneWayFares.length === 0) {
    return returnFares
  } else if (!returnFares || returnFares.length === 0) {
    return oneWayFares
  }

  return zipWith([oneWayFares, returnFares], (x, y) => x.concat(y))
}

const filterFares = (fares, legIndex) =>
  fares.filter(f => f.legs && f.legs.includes(legIndex))

const convertFaresPrices = (fares, currencyCode, currencyTypes) => {
  return fares.map(fare => {
    const newFare = { ...fare }
    newFare.price = convertToCurrency(
      newFare.price,
      newFare.currencyCode,
      currencyCode,
      currencyTypes
    )
    newFare.originalPrice = convertToCurrency(
      newFare.originalPrice,
      newFare.currencyCode,
      currencyCode,
      currencyTypes
    )
    newFare.currencyCode = currencyCode
    return newFare
  })
}

const getItineraryFromIndex = (itinIndex, isOutbound, response) => {
  return isOutbound
    ? response.outboundItineraries[itinIndex]
    : response.inboundItineraries[itinIndex]
}

const getLegsFromItinerary = (itinIndex, isOutbound, response) => {
  const itinerary = getItineraryFromIndex(itinIndex, isOutbound, response)
  return itinerary.legs.map((legId) => response.legs[legId])
}

const getStopCountFromItinerary = (itinIndex, isOutbound, response) => {
  const hops = getHopsFromItinerary(itinIndex, isOutbound, response)
  return hops.length - 1
}

const getHopsFromItinerary = (itinIndex, isOutbound, response) => {
  const legs = getLegsFromItinerary(itinIndex, isOutbound, response)
  const hopss = legs.map((leg) => leg.hops.map((hopId) => response.hops[hopId]))
  return flatten(hopss)
}

const getFirstHopFromItinerary = (itinIndex, isOutbound, response) => {
  const itinerary = getItineraryFromIndex(itinIndex, isOutbound, response)
  const firstLegIndex = itinerary.legs[0]
  const firstLeg = response.legs[firstLegIndex]
  const firstHopIndex = firstLeg.hops[0]
  return response.hops[firstHopIndex]
}

const getLastHopFromItinerary = (itinIndex, isOutbound, response) => {
  const itinerary = getItineraryFromIndex(itinIndex, isOutbound, response)
  const lastLegIndex = itinerary.legs[itinerary.legs.length - 1]
  const lastLeg = response.legs[lastLegIndex]
  const lastHopIndex = lastLeg.hops[lastLeg.hops.length - 1]
  return response.hops[lastHopIndex]
}

const getLongestHopFromItinerary = (itinIndex, isOutbound, response) => {
  const hops = getHopsFromItinerary(itinIndex, isOutbound, response)
  const longestHop = hops.reduce((acc, hop) => {
    if (acc.duration < hop.duration) {
      return hop
    }
    return acc
  }, hops[0])
  return longestHop
}

const getVehiclesFromItinerary = (itinIndex, isOutbound, response) => {
  const hops = getHopsFromItinerary(itinIndex, isOutbound, response)
  const nonTransferHops = hops.filter((hop) => !hop.isTransfer)
  return nonTransferHops.map((hop) => response.vehicles[hop.vehicle])
}

const getDepartureDateTimeFromItinerary = (itinIndex, isOutbound, response) => {
  const firstHop = getFirstHopFromItinerary(itinIndex, isOutbound, response)
  return new R2RDateTime(firstHop.departureDate, firstHop.departureTime)
}

const getArrivalDateTimeFromItinerary = (itinIndex, isOutbound, response) => {
  const lastHop = getLastHopFromItinerary(itinIndex, isOutbound, response)
  return new R2RDateTime(lastHop.arrivalDate, lastHop.arrivalTime)
}

const getDepartingLocationFromItinerary = (itinIndex, isOutbound, response) => {
  const firstHop = getFirstHopFromItinerary(itinIndex, isOutbound, response)
  const firstLine = response.lines[firstHop.line]
  const firstPlaceIndex = firstLine.places[0]
  return response.places[firstPlaceIndex].name
}

const getArrivalLocationFromItinerary = (itinIndex, isOutbound, response) => {
  const lastHop = getLastHopFromItinerary(itinIndex, isOutbound, response)
  const lastLine = response.lines[lastHop.line]
  const lastPlaceIndex = lastLine.places[lastLine.places.length - 1]
  return response.places[lastPlaceIndex].name
}

const getDepartingLocationFromLeg = (legIndex, response) => {
  const leg = response.legs[legIndex]
  const firstHopIndex = leg.hops[0]
  return getDepartingLocationFromHop(firstHopIndex, response)
}

const getArrivalLocationFromLeg = (legIndex, response) => {
  const leg = response.legs[legIndex]
  const lastHopIndex = leg.hops[leg.hops.length - 1]
  return getArrivalLocationFromHop(lastHopIndex, response)
}

const getDepartingLocationFromHop = (hopIndex, response) => {
  const hop = response.hops[hopIndex]
  const lineIndex = hop.line
  const line = response.lines[lineIndex]
  const firstPlaceIndex = line.places[0]
  const firstPlace = response.places[firstPlaceIndex]
  return firstPlace.name
}

const getArrivalLocationFromHop = (hopIndex, response) => {
  const line = getLineFromHop(hopIndex, response)
  const lastPlaceIndex = line.places[line.places.length - 1]
  const lastPlace = response.places[lastPlaceIndex]
  return lastPlace.name
}

const getLineFromHop = (hopIndex, response) => {
  const hop = response.hops[hopIndex]
  const lineIndex = hop.line
  return response.lines[lineIndex]
}

const getCarrierOfLongestHopFromItinerary = (itinIndex, isOutbound, response) => {
  const longestHop = getLongestHopFromItinerary(itinIndex, isOutbound, response)
  return response.carriers[longestHop.marketingCarrier]
}

const getIconFromItinerary = (itinIndex, isOutbound, response) => {
  const carrier = getCarrierOfLongestHopFromItinerary(itinIndex, isOutbound, response)
  return carrier ? carrier.iconUrl : null
}

const getOperatorNameFromItinerary = (itinIndex, isOutbound, response) => {
  const carrier = getCarrierOfLongestHopFromItinerary(itinIndex, isOutbound, response)
  return carrier ? carrier.name : null
}

const getProviderNameFromItinerary = (itinIndex, isOutbound, response) => {
  const itineraries = isOutbound ? response.outboundItineraries : response.inboundItineraries
  const bookingData =
      response.bookingData[itineraries[itinIndex].bookingDataIndex]
  return bookingData ? bookingData.providerName : null
}

const getCarrierCountFromItinerary = (itinIndex, isOutbound, response) => {
  const hops = getHopsFromItinerary(itinIndex, isOutbound, response)
  return new Set(hops.map((hop) => hop.marketingCarrier)).size
}

const getDurationFromItinerary = (itinIndex, isOutbound, response) => {
  const itinerary = getItineraryFromIndex(itinIndex, isOutbound, response)
  return itinerary.duration
}

const getOperatorDetailsFromItinerary = (itinIndex, isOutbound, response) => {
  const longestHop = getLongestHopFromItinerary(itinIndex, isOutbound, response)
  return response.carriers[longestHop.marketingCarrier]
}

const getDetailsLinkFromItinerary = (itinIndex, isOutbound, response) => {
  const compatibleDetailsLinks = response.detailsLinks.filter(
    (detailsLink) => (isOutbound
      ? detailsLink.outboundItinerary == itinIndex
      : detailsLink.inboundItinerary == itinIndex
    )
  )
  // Todo: this shouldn't be needed anymore
  // In order to get correct timelines, can use any compatible details link
  return compatibleDetailsLinks[0]
}

const getIsOvertakenFromItinerary = (itinIndex, isOutbound, response) => {
  const legs = getLegsFromItinerary(itinIndex, isOutbound, response)
  return contains(legs, l => l.isOvertaken)
}

// get hop modes that don't match the current tab
const getOtherTransitKindsFromItinerary = (itinIndex, isOutbound, response, modeId) => {
  const vehicles = getVehiclesFromItinerary(itinIndex, isOutbound, response)
  const transitKinds = vehicles.map((vehicle) => convertToTransitKind(vehicle.kind))
  const otherKinds = transitKinds.filter((vehicleMode) => vehicleMode !== modeId)
  return unique(otherKinds)
}

const getTimelineFromItinerary = (itinIndex, isOutbound, response) => {
  return createScheduledTimelineForTicketsItinerary(
    response, itinIndex, isOutbound
  )
}

const createItineraryForView = (
  response,
  modeId,
  itin,
  itinIndex,
  faress,
  isOutbound,
  transitKind,
) => {
  const departureDateTime = getDepartureDateTimeFromItinerary(itinIndex, isOutbound, response)
  const arrivalDateTime = getArrivalDateTimeFromItinerary(itinIndex, isOutbound, response)
  const departingLocation = getDepartingLocationFromItinerary(itinIndex, isOutbound, response)
  const arrivalLocation = getArrivalLocationFromItinerary(itinIndex, isOutbound, response)

  const icon = getIconFromItinerary(itinIndex, isOutbound, response)
  const stops = getStopCountFromItinerary(itinIndex, isOutbound, response)
  const operatorName = getOperatorNameFromItinerary(itinIndex, isOutbound, response)
  const hasMultipleCarriers = getCarrierCountFromItinerary(itinIndex, isOutbound, response) > 1
  const duration = getDurationFromItinerary(itinIndex, isOutbound, response)
  const operatorDetails = getOperatorDetailsFromItinerary(itinIndex, isOutbound, response)
  const providerName = getProviderNameFromItinerary(itinIndex, isOutbound, response)
  const detailsLink = getDetailsLinkFromItinerary(itinIndex, isOutbound, response)
  const isOvertaken = getIsOvertakenFromItinerary(itinIndex, isOutbound, response)
  const otherTransitKinds =
    getOtherTransitKindsFromItinerary(itinIndex, isOutbound, response, modeId)
  const itineraryId = itin.itineraryId
  const timeline = getTimelineFromItinerary(itinIndex, isOutbound, response)

  // MinPrice Calculations
  const minFares = faress.map(fares => arrayMin(fares, f => f.originalPrice, f => f))
  const minPrice = minFares.reduce((acc, f) => acc + f.originalPrice, 0)
  const minPriceFareClassNames = minFares.map((f) => f.name)
  // Assumption: all return fares are for one leg
  const minPriceIsReturnPrice = !!(minFares[0].legPairs && minFares[0].legPairs.length > 0)

  // bookingData is only required here if transitKind is shuttle/towncar
  const bookingData = (
    (TRANSITKINDS_WITH_DIRECT_OFFSITE.includes(transitKind)) ?
    constructBookingDataForTicket(
      response,
      itinIndex,
      faress
    ) : {}
  )

  const isDirectBooking = (bookingData.isDirectBooking === undefined)
     ? getIsDirectBooking(response, isOutbound, itinIndex)
     : bookingData.isDirectBooking

  return {
    // Stuff that the itinerary wants
    icon,
    hasMultipleCarriers,
    departureDateTime,
    arrivalDateTime,
    departingLocation,
    arrivalLocation,
    duration,
    stops,
    operatorName,
    modeId,
    itineraryId,
    detailsLink,
    minPrice,
    minPriceFareClassNames,
    minPriceIsReturnPrice,
    operatorDetails,
    transitKind,
    timeline,
    isOvertaken,
    otherTransitKinds,
    isDirectBooking,
    providerName,

    // Stuff that it's children need
    bookingData,
  }
}

const constructBookingDataForTicket = (response, itinIndex, faress) => {
  // Creating the booking data for the ticket
  const selectedOutboundItineraryProvider =
    response.bookingData[response.outboundItineraries[itinIndex].bookingDataIndex]

  const outboundFaresByAgent = groupFaresByAgent(
    [faress[0]],
    selectedOutboundItineraryProvider
  )

  const itinLegs = response.outboundItineraries[itinIndex]
    .legs.map(legIndex => response.legs[legIndex])

  const outboundBookingDataPerAgent = constructBookingDataPerAgent(
    outboundFaresByAgent,
    itinLegs,
    selectedOutboundItineraryProvider,
  )
  const outboundTotalPriceByAgent = calculatePerAgent(outboundFaresByAgent)

  // Transfers will always be jayride currently
  const outboundAgentKeys = Object.keys(outboundFaresByAgent)
  const agentKey = outboundAgentKeys[0]

  return ({
    agentName: agentKey,
    outboundBookingData: outboundBookingDataPerAgent[agentKey],
    totalPrice: outboundTotalPriceByAgent[agentKey],
    isDirectBooking: outboundFaresByAgent.isDirectBooking,
  })
}

const getIsDirectBooking = (response, isOutbound, itinIndex) => {
  const itinerary = getItineraryFromIndex(itinIndex, isOutbound, response)
  return itinerary ? response.bookingData[itinerary.bookingDataIndex].isDirectBooking : false
}

const getTabHasResults = (isReturn, outboundResults, inboundResults) => {
  const hasOutbound = outboundResults && outboundResults.length > 0
  const hasInbound = inboundResults && inboundResults.length > 0

  return isReturn ? hasOutbound && hasInbound : hasOutbound
}

const getCheapestLegPairPrice = (fares) => {
  const cheapestFare = getCheapestLegPairFare(fares)
  return cheapestFare ? cheapestFare.originalPrice : null
}

const getCheapestLegPairFare = (fares) =>
  // Assumption: Results involving leg pairs will only have itineraries that contain one
  // outbound leg and one inbound leg. Thus to calculate the minimum price is just the
  // minimum price over fares that have leg pairs
  fares.reduce((acc, fare) => {
    if (fare.legPairs && fare.legPairs.length) {
      if (!acc) {
        return fare
      }
      return acc.originalPrice < fare.originalPrice ? acc : fare
    }
    return acc
  }, null)


const getCheapestOneWayLegFare = (legId, fares) =>
  fares.reduce((acc, fare) => {
    if (fare.legs && fare.legs.includes(legId)) {
      if (!acc) {
        return fare
      }
      return acc.originalPrice < fare.originalPrice ? acc : fare
    }
    return acc
  }, null)


const getCheapestOneWayItineraryFares = (itinerary, fares) => {
  const cheapestFares = itinerary.legs.map((legId) => getCheapestOneWayLegFare(legId, fares))

  // If any of the legs don't have one-way fares, return null
  return cheapestFares.includes(null) ? null : cheapestFares
}

const addFares = (fares) => {
  const minPrice = fares.reduce((price, fare) => {
    if (price === -1) {
      return fare.originalPrice
    }
    return price + fare.originalPrice
  }, -1)
  return minPrice
}

const getCheapestOneWayPrice = (itineraries, fares) =>
  itineraries.reduce((acc, itin) => {
    const cheapestFares = getCheapestOneWayItineraryFares(itin, fares)
    if (!cheapestFares) {
      return acc
    }

    const minPrice = addFares(cheapestFares)
    if (acc === null) {
      return minPrice
    }
    return Math.min(acc, minPrice)
  }, null)

const getTabMinPrice = (
  outboundItineraries,
  inboundItineraries,
  fares,
  isReturn,
  currencyCode,
  currencyTypes,
) => {
  if (!fares.length) {
    return null
  }

  const minOutboundPrice = getCheapestOneWayPrice(outboundItineraries, fares)
  const minInboundPrice = getCheapestOneWayPrice(inboundItineraries, fares)
  const minLegPairPrice = getCheapestLegPairPrice(fares)

  let minPrice
  if (isReturn) {
    if (minOutboundPrice && minInboundPrice) {
      minPrice = minOutboundPrice + minInboundPrice
    }
    if (minLegPairPrice) {
      minPrice = minPrice ? Math.min(minPrice, minLegPairPrice) : minLegPairPrice
    }
  } else {
    minPrice = minOutboundPrice
  }
  // Assumption: all fares of same transport mode will have same currency code
  const fareCurrencyCode = fares[0].currencyCode

  return convertToCurrency(
    minPrice,
    fareCurrencyCode,
    currencyCode,
    currencyTypes
  )
}

const getItineraryOriginDestination = (response, itinerary) => {
  const firstLeg = response.legs[itinerary.legs[0]]
  const firstHop = response.hops[firstLeg.hops[0]]
  const firstLine = response.lines[firstHop.line]
  const firstPlace = response.places[firstLine.places[0]]
  const departingLocation = firstPlace.name

  const lastLeg = response.legs[itinerary.legs[itinerary.legs.length - 1]]
  const lastHop = response.hops[lastLeg.hops[lastLeg.hops.length - 1]]
  const lastLine = response.lines[lastHop.line]
  const lastPlace = response.places[lastLine.places[lastLine.places.length - 1]]
  const arrivalLocation = lastPlace.name

  return {
    departingLocation,
    arrivalLocation,
  }
}


/* ****** BOOK WITH CALCULATIONS *********/

const getBookOptions = createSelector(
  [
    getSelectedFaresAreReturn,
    getFaresForSelections,
    getSelectedOutboundItineraryProvider,
    getSelectedOutboundItineraryLegs,
    getSelectedInboundItineraryProvider,
    getSelectedInboundItineraryLegs,
    getOutboundAndInboundBookableTogether,
    getOriginShortName,
    getDestinationShortName,
  ],
  (
    selectedFaresAreReturn,
    faresForSelections,
    selectedOutboundItineraryProvider,
    selectedOutboundItineraryLegs,
    selectedInboundItineraryProvider,
    selectedInboundItineraryLegs,
    outboundAndInboundBookableTogether,
    originShortName,
    destinationShortName,
  ) => {
    const options = []
    // This looks scary, but we're just organising all our information by agent
    if (selectedFaresAreReturn) {
      // Return tickets
      const returnFaresByAgent = groupFaresByAgent(
        faresForSelections.returnFares,
        selectedOutboundItineraryProvider,
      )
      const returnBookingDataPerAgent = constructBookingDataPerAgent(
        returnFaresByAgent,
        selectedOutboundItineraryLegs,
        selectedOutboundItineraryProvider
      )
      const returnTotalPriceByAgent = calculatePricePerAgent(returnFaresByAgent)
      const returnOriginalPriceByAgent = calculateOriginalPricePerAgent(returnFaresByAgent)
      const returnAgentKeys = Object.keys(returnFaresByAgent)
      // Loop through possible agents and build out their bookingObj
      returnAgentKeys.forEach(agentKey => {
        const returnBookWith = {
          agentName: agentKey,
          returnBookingDatas: returnBookingDataPerAgent[agentKey],
          totalPrice: returnTotalPriceByAgent[agentKey],
          originalPrice: returnOriginalPriceByAgent[agentKey],
          isDirectBooking: selectedOutboundItineraryProvider.isDirectBooking,
          primaryLabel: agentKey,
        }
        options.push(returnBookWith)
      })
    } else if (outboundAndInboundBookableTogether) {
      // One way combinable tickets
      // Outbound Ticket
      const outboundFaresByAgent = groupFaresByAgent(
        faresForSelections.oneWayOutboundFares,
        selectedOutboundItineraryProvider
      )
      const outboundBookingDataPerAgent = constructBookingDataPerAgent(
        outboundFaresByAgent,
        selectedOutboundItineraryLegs,
        selectedOutboundItineraryProvider
      )
      // Inbound Ticket
      const inboundFaresByAgent = groupFaresByAgent(
        faresForSelections.oneWayInboundFares,
        selectedInboundItineraryProvider
      )
      const inboundBookingDataPerAgent = constructBookingDataPerAgent(
        inboundFaresByAgent,
        selectedInboundItineraryLegs,
        selectedInboundItineraryProvider
      )
      // get Outbound AND Inbound Prices AND then combine them
      const outboundPrices = calculatePricePerAgent(outboundFaresByAgent)
      const inboundPrices = calculatePricePerAgent(inboundFaresByAgent)
      const outboundOriginalPrices = calculateOriginalPricePerAgent(outboundFaresByAgent)
      const inboundOriginalPrices = calculateOriginalPricePerAgent(inboundFaresByAgent)
      const totalPriceByAgent = Object.keys(outboundPrices).reduce((acc, agentName) => {
        acc[agentName] = outboundPrices[agentName] + inboundPrices[agentName]
        return acc
      }, {})
      const originalPriceByAgent = Object.keys(outboundOriginalPrices).reduce((acc, agentName) => {
        acc[agentName] = outboundOriginalPrices[agentName] + inboundOriginalPrices[agentName]
        return acc
      }, {})
      const agentKeys = Object.keys(outboundFaresByAgent)
      // Loop through possible agents and build out their bookingObj
      agentKeys.forEach(agentKey => {
        const bookWith = {
          agentName: agentKey,
          inboundBookingDatas: inboundBookingDataPerAgent[agentKey],
          outboundBookingDatas: outboundBookingDataPerAgent[agentKey],
          totalPrice: totalPriceByAgent[agentKey],
          originalPrice: originalPriceByAgent[agentKey],
          isDirectBooking: selectedOutboundItineraryProvider.isDirectBooking,
          primaryLabel: agentKey,
        }
        options.push(bookWith)
      })
    } else {
      // One-way, non-combinable tickets
      // Process Outbound ticket
      const outboundFaresByAgent = groupFaresByAgent(
        faresForSelections.oneWayOutboundFares,
        selectedOutboundItineraryProvider

      )
      const outboundBookingDataPerAgent = constructBookingDataPerAgent(
        outboundFaresByAgent,
        selectedOutboundItineraryLegs,
        selectedOutboundItineraryProvider,
      )
      const outboundTotalPriceByAgent = calculatePricePerAgent(outboundFaresByAgent)
      const outboundOriginalPriceByAgent = calculateOriginalPricePerAgent(outboundFaresByAgent)

      const outboundAgentKeys = Object.keys(outboundFaresByAgent)
      // Loop through possible agents and build out their bookingObj
      outboundAgentKeys.forEach(agentKey => {
        const outboundBookWith = {
          agentName: agentKey,
          outboundBookingDatas: outboundBookingDataPerAgent[agentKey],
          totalPrice: outboundTotalPriceByAgent[agentKey],
          originalPrice: outboundOriginalPriceByAgent[agentKey],
          isDirectBooking: selectedOutboundItineraryProvider.isDirectBooking,
          primaryLabel: agentKey,
          isOnlyOutbound: (selectedInboundItineraryProvider !== null),
          isOutbound: true,
          secondaryLabel: {
            originShortName,
            destinationShortName,
          },
        }
        options.push(outboundBookWith)
      })

      // If an inbound provider also exists
      // Process inbound Ticket
      if (selectedInboundItineraryProvider) {
        const inboundFaresByAgent = groupFaresByAgent(
          faresForSelections.oneWayInboundFares,
          selectedInboundItineraryProvider
        )
        const inboundBookingDataPerAgent = constructBookingDataPerAgent(
          inboundFaresByAgent,
          selectedInboundItineraryLegs,
          selectedInboundItineraryProvider,
        )
        const inboundTotalPriceByAgent = calculatePricePerAgent(inboundFaresByAgent)
        const inboundOriginalPriceByAgent = calculateOriginalPricePerAgent(inboundFaresByAgent)
        const inboundAgentKeys = Object.keys(inboundFaresByAgent)
        // Loop through possible agents and build out their bookingObj
        inboundAgentKeys.forEach(agentKey => {
          const inboundBookWith = {
            agentName: agentKey,
            inboundBookingDatas: inboundBookingDataPerAgent[agentKey],
            totalPrice: inboundTotalPriceByAgent[agentKey],
            originalPrice: inboundOriginalPriceByAgent[agentKey],
            isDirectBooking: selectedInboundItineraryProvider.isDirectBooking,
            isOnlyInbound: true,
            secondaryLabel: {
              originShortName: destinationShortName,
              destinationShortName: originShortName,
            },
          }
          options.push(inboundBookWith)
        })
      }
    }
    return options
  }
)

const getBookDeals = createSelector(
  [
    getSelectedFaresAreReturn,
    getFaresForSelections,
    getSelectedOutboundItineraryProvider,
    getSelectedOutboundItineraryLegs,
    getSelectedInboundItineraryProvider,
    getSelectedInboundItineraryLegs,
    getOutboundAndInboundBookableTogether,
  ],
  (
    selectedFaresAreReturn,
    faresForSelections,
    selectedOutboundItineraryProvider,
    selectedOutboundItineraryLegs,
    selectedInboundItineraryProvider,
    selectedInboundItineraryLegs,
    outboundAndInboundBookableTogether,
  ) => {
    const options = []
    // This looks scary, but we're just organising all our information by agent
    if (selectedFaresAreReturn) {
      // Return tickets
      const returnFaresByAgent = groupFaresByAgent(
        faresForSelections.returnFares,
        selectedOutboundItineraryProvider,
      )
      const returnBookingDataPerAgent = constructBookingDataPerAgent(
        returnFaresByAgent,
        selectedOutboundItineraryLegs,
        selectedOutboundItineraryProvider
      )
      const returnTotalPriceByAgent = calculatePricePerAgent(returnFaresByAgent)
      const returnOriginalPriceByAgent = calculateOriginalPricePerAgent(returnFaresByAgent)
      const returnAgentKeys = Object.keys(returnFaresByAgent)
      // Loop through possible agents and build out their bookingObj
      returnAgentKeys.forEach(agentKey => {
        const returnBookOption = {
          agent: agentKey,
          price: returnTotalPriceByAgent[agentKey],
          originalPrice: returnOriginalPriceByAgent[agentKey],
          bookingData: {
            return: returnBookingDataPerAgent[agentKey],
          },
          isDirectBooking: selectedOutboundItineraryProvider.isDirectBooking,
        }
        options.push(returnBookOption)
      })
    } else if (outboundAndInboundBookableTogether) {
      // One way combinable tickets
      // Outbound Ticket
      const outboundFaresByAgent = groupFaresByAgent(
        faresForSelections.oneWayOutboundFares,
        selectedOutboundItineraryProvider
      )
      const outboundBookingDataPerAgent = constructBookingDataPerAgent(
        outboundFaresByAgent,
        selectedOutboundItineraryLegs,
        selectedOutboundItineraryProvider
      )
      // Inbound Ticket
      const inboundFaresByAgent = groupFaresByAgent(
        faresForSelections.oneWayInboundFares,
        selectedInboundItineraryProvider
      )
      const inboundBookingDataPerAgent = constructBookingDataPerAgent(
        inboundFaresByAgent,
        selectedInboundItineraryLegs,
        selectedInboundItineraryProvider
      )
      // get Outbound AND Inbound Prices AND then combine them
      const outboundPrices = calculatePricePerAgent(outboundFaresByAgent)
      const inboundPrices = calculatePricePerAgent(inboundFaresByAgent)
      const outboundOriginalPrices = calculateOriginalPricePerAgent(outboundFaresByAgent)
      const inboundOriginalPrices = calculateOriginalPricePerAgent(inboundFaresByAgent)
      const totalPriceByAgent = Object.keys(outboundPrices).reduce((acc, agentName) => {
        acc[agentName] = outboundPrices[agentName] + inboundPrices[agentName]
        return acc
      }, {})
      const originalPriceByAgent = Object.keys(outboundOriginalPrices).reduce((acc, agentName) => {
        acc[agentName] = outboundOriginalPrices[agentName] + inboundOriginalPrices[agentName]
        return acc
      }, {})
      const agentKeys = Object.keys(outboundFaresByAgent)
      // Loop through possible agents and build out their bookingObj
      agentKeys.forEach(agentKey => {
        const bookOption = {
          agent: agentKey,
          price: totalPriceByAgent[agentKey],
          originalPrice: originalPriceByAgent[agentKey],
          bookingData: {
            inbound: inboundBookingDataPerAgent[agentKey],
            outbound: outboundBookingDataPerAgent[agentKey],
          },
          isDirectBooking: selectedOutboundItineraryProvider.isDirectBooking,
        }
        options.push(bookOption)
      })
    } else {
      // One-way, non-combinable tickets
      // Process Outbound ticket
      const outboundFaresByAgent = groupFaresByAgent(
        faresForSelections.oneWayOutboundFares,
        selectedOutboundItineraryProvider

      )
      const outboundBookingDataPerAgent = constructBookingDataPerAgent(
        outboundFaresByAgent,
        selectedOutboundItineraryLegs,
        selectedOutboundItineraryProvider,
      )
      const outboundTotalPriceByAgent = calculatePricePerAgent(outboundFaresByAgent)
      const outboundOriginalPriceByAgent = calculateOriginalPricePerAgent(outboundFaresByAgent)

      const outboundAgentKeys = Object.keys(outboundFaresByAgent)
      // Loop through possible agents and build out their bookingObj
      outboundAgentKeys.forEach(agentKey => {
        const outboundBookOption = {
          agent: agentKey,
          price: outboundTotalPriceByAgent[agentKey],
          originalPrice: outboundOriginalPriceByAgent[agentKey],
          bookingData: {
            outbound: outboundBookingDataPerAgent[agentKey],
          },
          isDirectBooking: selectedOutboundItineraryProvider.isDirectBooking,
          isOutbound: true,
        }
        options.push(outboundBookOption)
      })

      // If an inbound provider also exists
      // Process inbound Ticket
      if (selectedInboundItineraryProvider) {
        const inboundFaresByAgent = groupFaresByAgent(
          faresForSelections.oneWayInboundFares,
          selectedInboundItineraryProvider
        )
        const inboundBookingDataPerAgent = constructBookingDataPerAgent(
          inboundFaresByAgent,
          selectedInboundItineraryLegs,
          selectedInboundItineraryProvider,
        )
        const inboundTotalPriceByAgent = calculatePricePerAgent(inboundFaresByAgent)
        const inboundOriginalPriceByAgent = calculateOriginalPricePerAgent(inboundFaresByAgent)
        const inboundAgentKeys = Object.keys(inboundFaresByAgent)
        // Loop through possible agents and build out their bookingObj
        inboundAgentKeys.forEach(agentKey => {
          const inboundBookOption = {
            agent: agentKey,
            price: inboundTotalPriceByAgent[agentKey],
            originalPrice: inboundOriginalPriceByAgent[agentKey],
            bookingData: {
              inbound: inboundBookingDataPerAgent[agentKey],
            },
            isDirectBooking: selectedInboundItineraryProvider.isDirectBooking,
          }
          options.push(inboundBookOption)
        })
      }
    }
    return options
  }
)

const getOutboundBookDeals = createSelector(
  [getBookDeals],
  (allDeals) => allDeals.filter((deal) => deal.isOutbound)
)

// includes return
const getInboundBookDeals = createSelector(
  [getBookDeals],
  (allDeals) => allDeals.filter((deal) => !deal.isOutbound)
)

const getSelectedOutboundBestDeal = createSelector(
  [getOutboundBookDeals],
  (deals) => deals[0]
)

const getSelectedInboundBestDeal = createSelector(
  [getInboundBookDeals],
  (deals) => deals[0]
)

const getSelectedOutboundOtherDeals = createSelector(
  [getOutboundBookDeals],
  (deals) => deals.slice(1)
)

const getSelectedInboundOtherDeals = createSelector(
  [getInboundBookDeals],
  (deals) => deals.slice(1)
)

const getSelectedOutboundHasOtherDeals = createSelector(
  [getSelectedOutboundOtherDeals],
  (otherDeals) => otherDeals.length > 1
)

const getSelectedInboundHasOtherDeals = createSelector(
  [getSelectedInboundOtherDeals],
  (otherDeals) => otherDeals.length > 1
)

const getAreTicketsCombinable = createSelector(
  [
    getSelectedFaresAreReturn,
    getOutboundAndInboundBookableTogether,
    getSelectedInboundItineraryProvider,
  ],
  (
    selectedFaresAreReturn,
    outboundAndInboundBookableTogether,
    selectedInboundItineraryProvider,
  ) => {
    return (
      selectedFaresAreReturn
      || outboundAndInboundBookableTogether
      || !selectedInboundItineraryProvider
    )
  }
)

const calculatePerAgent = (faresByAgent, originalPrice = false) => {
  return Object.keys(faresByAgent).reduce((acc, agentName) => {
    const fares = faresByAgent[agentName]
    acc[agentName] = fares.reduce(
      (sum, f) => sum + f[(originalPrice) ? 'originalPrice' : 'price']
      , 0)
    return acc
  }, {})
}

const calculatePricePerAgent = (faresByAgent) => calculatePerAgent(faresByAgent)
const calculateOriginalPricePerAgent = (faresByAgent) => calculatePerAgent(faresByAgent, true)

// Create booking data per agent
// note: faress[i] are the possible fares for legs[i]
const constructBookingDataPerAgent = (faresByAgent, legs, provider) => {
  // const faresByAgent = groupFaresByAgent(faress, provider)

  const bookingDataByAgent = {}
  Object.keys(faresByAgent).forEach((agentName) => {
    bookingDataByAgent[agentName] =
      constructBookingData(faresByAgent[agentName], legs, provider)
  })

  return bookingDataByAgent
}

// This takes a list of fares for each leg and returns a list of fares for each for each agent
// If an agent has two fares for a leg, only the cheapest one will be kept
//  - If the provider is bookable on Rome2rio, the agent will be "Rome2rio"
//  - If the fare has an agent name, the agent will be that: eg "Kiwi.com"
//  - For non-direct Distribusion fares, the agent is the operator's Distribusion white-label
//  - Otherwise the agent is the provider
// faress[i] is an array of fares for leg i
// output['agent'] = [fareForLeg0, fareForLeg1, fareForLeg2]
const groupFaresByAgent = (faress, provider) => {
  const faresByAgent = faress.reduce((acc, fares, legNumber) => {
    fares.forEach((fare => {
      let key
      if (provider.isDirectBooking) {
        key = 'Rome2rio'
      } else if (fare.agentName != null) {
        key = fare.agentName
      } else if (provider.providerName === 'Distribusion') {
        key = 'the Operator'
      } else {
        key = provider.providerName
      }

      if (!acc[key]) {
        acc[key] = []
      }

      if (!acc[key][legNumber]) {
        acc[key][legNumber] = fare
      } else {
        const existingFare = acc[key][legNumber]
        acc[key][legNumber] = existingFare.price < fare.price ? existingFare : fare
      }
    }))

    return acc
  }, {})

  return faresByAgent
}

// Create booking data to be used for checkout, given a list of fares
// for a list of legs, with a provider
const constructBookingData = (fares, legs, provider) => {
  const legAndFareBookingData = zipWith([fares, legs], (f, l) =>
    ({
      legBookingData: l.bookingData,
      fareBookingData: f.fareBookingData,
    })
  )

  return {
    legAndFareBookingData,
    providerBookingData: provider,
  }
}

// Determines if there is a minimum buffer period between an outbound journey arriving and an
// inbound journey departing
const isAfterBufferPeriod = (
  outboundResponse,
  outboundItinIndex,
  inboundResponse,
  inboundItinIndex,
) => {
  const outboundItin = outboundResponse.outboundItineraries[outboundItinIndex]
  const lastLeg = outboundResponse.legs[outboundItin.legs[outboundItin.legs.length - 1]]
  const lastHop = outboundResponse.hops[lastLeg.hops[lastLeg.hops.length - 1]]
  const arrivalTime = new R2RDateTime(lastHop.arrivalDate, lastHop.arrivalTime)

  const inboundItin = inboundResponse.inboundItineraries[inboundItinIndex]
  const firstLeg = inboundResponse.legs[inboundItin.legs[0]]
  const firstHop = inboundResponse.hops[firstLeg.hops[0]]
  const departureTime = new R2RDateTime(firstHop.departureDate, firstHop.departureTime)

  return departureTime.isAfter(arrivalTime, MIN_BUFFER_MINUTES)
}

export {
  getRawSearchResponses,
  getSearchResponses,
  getSelectedModeSearchResponse,
  getSelectedModeRequestId,
  getSelectedOutboundSearchResponse,
  getSelectedInboundSearchResponse,
  getSelectedOutboundItineraryLegs,
  getSelectedInboundItineraryLegs,
  getSelectedOutboundItineraryProvider,
  getSelectedInboundItineraryProvider,
  getSelectedOutboundItineraryLegTitles,
  getSelectedInboundItineraryLegTitles,
  getSelectedOutboundFareExtraCost,
  getSelectedInboundFareExtraCost,
  getSelectedOutboundIsDirectBooking,
  getSelectedInboundIsDirectBooking,
  getSelectedOutboundDepartureDateTime,
  getSelectedInboundDepartureDateTime,
  getSelectedOutboundArrivalDateTime,
  getSelectedInboundArrivalDateTime,
  getSelectedOutboundDuration,
  getSelectedInboundDuration,
  getSelectedOutboundLogo,
  getSelectedInboundLogo,
  getSelectedOutboundFareRulesSummary,
  getSelectedInboundFareRulesSummary,
  getSelectedOutboundHasMultipleCarriers,
  getSelectedInboundHasMultipleCarriers,
  getSelectedOutboundOperatorName,
  getSelectedInboundOperatorName,
  getSelectedOutboundStopCount,
  getSelectedOutboundMinPrice,
  getSelectedInboundMinPrice,
  getSelectedOutboundFareIndices,
  getSelectedInboundFareIndices,
  getSelectedOutboundHasOtherDeals,
  getSelectedInboundHasOtherDeals,
  getOutboundAndInboundBookableTogether,
  getSelectedInboundStopCount,
  getSelectedOutboundTimeline,
  getSelectedInboundTimeline,
  getSelectedOutboundHasFareOptions,
  getSelectedInboundHasFareOptions,
  getSelectedOutboundFareNames,
  getSelectedInboundFareNames,
  getSelectedOutboundFareRules,
  getSelectedInboundFareRules,
  getSelectedOutboundDepartingLocation,
  getSelectedInboundDepartingLocation,
  getSelectedOutboundArrivalLocation,
  getSelectedInboundArrivalLocation,
  getActiveItineraries,
  getIsSelectingInbound,
  getSelectedInboundBasePrice,
  getFaresForSelectedItineraries,
  getTotalCost,
  getActiveModeHasNoResults,
  getOutboundTicket,
  getInboundTicket,
  getSelectedOutboundOriginDestination,
  getSelectedInboundOriginDestination,
  getSelectedFaresAreReturn,
  getTabs,
  showOutboundDirection,
  showInboundDirection,
  getLastOutboundTicketTime,
  getLastInboundTicketTime,
  getSelectedOutboundItineraryIndex,
  getSelectedInboundItineraryIndex,
  getDetailsLinkForSelections,
  getNeedsDetailsLookup,
  getBookOptions,
  getSelectedOutboundBestDeal,
  getSelectedInboundBestDeal,
  getSelectedOutboundOtherDeals,
  getSelectedInboundOtherDeals,
  getAreTicketsCombinable,
  getActiveModeIsLoadingOutbound,
  getActiveModeIsLoadingInbound,
  getActiveModeIsLoading,
  getCartType,
  getOutboundCartShouldShowBookDeal,
  getOutboundCartShouldShowFareInfo,
  getShouldShowCart,
  getIsInvalidHistoryState,
  getDepartureDateTimeFromItinerary,
}
